import { Link } from "react-router-dom";
import { Linkedin, Twitter } from "lucide-react";
import { Logo } from "@/components/brand/Logo";

interface FooterColumn {
  heading: string;
  links: { label: string; to: string; external?: boolean }[];
}

const COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "Features", to: "/features" },
      { label: "Pricing", to: "/pricing" },
      { label: "How it works", to: "/how-it-works" },
      { label: "Roadmap", to: "/roadmap" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Careers", to: "/careers" },
      { label: "Blog", to: "/blog" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Docs", to: "/docs" },
      { label: "Changelog", to: "/changelog" },
      { label: "Status", to: "/status" },
      { label: "Security", to: "/security" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
      { label: "DPA", to: "/dpa" },
    ],
  },
];

export function Footer() {
  return (
    <footer className="border-t border-white/10 bg-brand-midnight">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Logo />
            <p className="mt-4 text-sm text-brand-ink/60 max-w-xs">
              Hire your next teammate. Skip the offer letter.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a
                href="https://twitter.com/virtualdeskai"
                aria-label="VirtualDesk on X"
                className="text-brand-ink/60 hover:text-brand-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet rounded-sm"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://linkedin.com/company/virtualdesk-ai"
                aria-label="VirtualDesk on LinkedIn"
                className="text-brand-ink/60 hover:text-brand-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet rounded-sm"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
                {col.heading}
              </h3>
              <ul className="mt-4 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-sm text-brand-ink/70 hover:text-brand-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet rounded-sm"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-xs text-brand-ink/50">
            &copy; {new Date().getFullYear()} VirtualDesk.ai. All rights reserved.
          </p>
          <p className="text-xs text-brand-ink/40">
            Built for teams that want to move faster than a hiring pipeline.
          </p>
        </div>
      </div>
    </footer>
  );
}
